import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Loader2, ShieldAlert, MessagesSquare, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import GroupChannelListModule from "@sendbird/uikit-react/GroupChannelList";
import GroupChannelModule from "@sendbird/uikit-react/GroupChannel";

import Layout from "@/components/layout/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { useSendbird } from "@/hooks/useSendbird";
import { SENDBIRD_APP_ID } from "@/lib/sendbird";
import { api } from "@/api/client";
import "./chat-sendbird.css";

const CUSTOM_TYPE_LABEL = {
    rx_verification: { label: "Rx Verification", color: "bg-emerald-100 text-emerald-700" },
    doctor_consult: { label: "Doctor Consult", color: "bg-sky-100 text-sky-700" },
    support_ticket: { label: "Support", color: "bg-amber-100 text-amber-700" },
    ai_assistant: { label: "AI Assistant", color: "bg-violet-100 text-violet-700" },
    notifications: { label: "Notifications", color: "bg-rose-100 text-rose-700" },
};

const ChannelListPreview = ({ channel, onClick, active }) => {
    const custom = CUSTOM_TYPE_LABEL[channel.customType] || { label: channel.customType || "Chat", color: "bg-slate-100 text-slate-700" };
    const last = channel.lastMessage;
    const unread = channel.unreadMessageCount || 0;
    return (
        <button
            type="button"
            onClick={() => onClick(channel)}
            className={`w-full rounded-xl border p-3 text-left transition-all ${
                active
                    ? "border-brand-300 bg-brand-50 dark:border-brand-700 dark:bg-brand-950/30"
                    : "border-slate-200 bg-white hover:border-brand-200 hover:bg-slate-50 dark:border-white/10 dark:bg-white/[0.03] dark:hover:bg-white/[0.06]"
            }`}
            data-testid={`chat-channel-${channel.customType}-${channel.url.slice(-8)}`}
            data-channel-custom-type={channel.customType}
        >
            <div className="mb-1 flex items-center justify-between gap-2">
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${custom.color}`}>
                    {custom.label}
                </span>
                {unread > 0 && (
                    <span className="inline-flex min-w-[18px] items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-semibold text-white">
                        {unread > 99 ? "99+" : unread}
                    </span>
                )}
            </div>
            <div className="line-clamp-1 text-sm font-semibold tracking-tight text-slate-900 dark:text-zinc-100">
                {channel.name || "PILLS Chat"}
            </div>
            <div className="mt-0.5 line-clamp-1 text-xs text-slate-500 dark:text-zinc-400">
                {last?.message || <span className="italic">No messages yet</span>}
            </div>
        </button>
    );
};

const Content = () => {
    const { ready, session, error, refreshUnread } = useSendbird();
    const [selectedChannel, setSelectedChannel] = useState(null);
    const [aiThinking, setAiThinking] = useState(false);
    const aiInFlightRef = useRef(0);

    useEffect(() => {
        if (ready) refreshUnread();
    }, [ready, refreshUnread]);

    // v2a fix — trigger backend Gemini reply whenever the user sends a message
    // into their AI Assistant channel. The user's own message goes to Sendbird
    // via the UIKit's own send path (instant local echo); this hook fires the
    // parallel backend call which posts Gemini's reply as `pills_ai_bot`.
    const triggerAiReply = useCallback(async (userMessage) => {
        aiInFlightRef.current += 1;
        setAiThinking(true);
        try {
            await api.post("/sendbird/ai/reply", { user_message: userMessage });
        } catch (err) {
            console.warn("[chat] AI reply endpoint failed:", err?.response?.data || err?.message);
        } finally {
            aiInFlightRef.current -= 1;
            if (aiInFlightRef.current <= 0) {
                aiInFlightRef.current = 0;
                setAiThinking(false);
            }
        }
    }, []);

    const onBeforeSendUserMessage = useCallback((params) => {
        // Only intercept for the AI Assistant channel
        if (selectedChannel?.customType === "ai_assistant") {
            const text = params?.message || "";
            if (text.trim()) {
                // fire-and-forget — the SDK still sends via its normal path
                triggerAiReply(text);
            }
        }
        return params;
    }, [selectedChannel, triggerAiReply]);

    const queryParams = useMemo(() => ({
        customTypesFilter: [
            "rx_verification",
            "doctor_consult",
            "support_ticket",
            "ai_assistant",
            "notifications",
        ],
        limit: 40,
        includeEmpty: true,
        order: "latest_last_message",
    }), []);

    if (!SENDBIRD_APP_ID) {
        return (
            <div className="mx-auto max-w-2xl px-6 py-24 text-center">
                <ShieldAlert className="mx-auto h-10 w-10 text-amber-500" />
                <h1 className="mt-4 text-xl font-semibold tracking-tight text-slate-900">Chat is not configured</h1>
                <p className="mt-2 text-sm text-slate-500">Set <code className="rounded bg-slate-100 px-1">REACT_APP_SENDBIRD_APP_ID</code> in your frontend environment.</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="mx-auto max-w-2xl px-6 py-24 text-center" data-testid="chat-error">
                <ShieldAlert className="mx-auto h-10 w-10 text-rose-500" />
                <h1 className="mt-4 text-xl font-semibold tracking-tight text-slate-900">Chat service is unavailable</h1>
                <p className="mt-2 text-sm text-slate-500">
                    We couldn&apos;t connect to Sendbird right now. Please refresh in a moment. If the issue continues, our team has been notified.
                </p>
                <p className="mt-2 text-xs text-slate-400">{String(error?.message || error)}</p>
            </div>
        );
    }

    if (!ready || !session) {
        return (
            <div className="grid place-items-center py-24 text-slate-500" data-testid="chat-loading">
                <Loader2 className="mb-3 h-8 w-8 animate-spin text-brand-600" />
                <p className="text-sm">Connecting to PILLS Chat…</p>
            </div>
        );
    }

    return (
        <div className="mx-auto grid h-[calc(100vh-8rem)] w-full max-w-7xl gap-4 px-4 py-6 lg:grid-cols-[340px_1fr]" data-testid="chat-sendbird-page">
                <div className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-soft dark:border-white/10 dark:bg-white/[0.03]">
                    <header className="border-b border-slate-100 px-4 py-3 dark:border-white/10">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Conversations</p>
                        <h2 className="text-sm font-semibold text-slate-900 dark:text-zinc-100">Rx • Consults • Support • AI</h2>
                    </header>
                    <div className="min-h-0 flex-1 overflow-y-auto p-2" data-testid="chat-channel-list">
                        <GroupChannelListModule
                            channelListQueryParams={queryParams}
                            onChannelSelect={(ch) => setSelectedChannel(ch)}
                            disableAutoSelect={false}
                            renderChannelPreview={({ channel, onLeaveChannel }) => (
                                <ChannelListPreview
                                    channel={channel}
                                    onClick={setSelectedChannel}
                                    active={selectedChannel?.url === channel.url}
                                />
                            )}
                            renderHeader={() => (
                                <div className="hidden" />
                            )}
                        />
                    </div>
                </div>

                <div className="relative flex min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-soft dark:border-white/10 dark:bg-white/[0.03]" data-testid="chat-active-channel">
                    {selectedChannel ? (
                        <>
                            <GroupChannelModule
                                channelUrl={selectedChannel.url}
                                onBeforeSendUserMessage={onBeforeSendUserMessage}
                                onChatHeaderActionClick={() => {}}
                                onBackClick={() => setSelectedChannel(null)}
                            />
                            {selectedChannel.customType === "ai_assistant" && aiThinking && (
                                <div
                                    className="pointer-events-none absolute bottom-24 left-1/2 z-10 -translate-x-1/2 rounded-full border border-violet-200 bg-white/95 px-4 py-2 shadow-lg backdrop-blur dark:border-violet-500/30 dark:bg-zinc-900/95"
                                    data-testid="ai-assistant-typing"
                                >
                                    <span className="flex items-center gap-2 text-xs font-medium text-violet-700 dark:text-violet-300">
                                        <Sparkles className="h-3.5 w-3.5 animate-pulse" />
                                        PILLS AI Assistant is typing
                                        <span className="inline-flex gap-0.5">
                                            <span className="h-1 w-1 animate-bounce rounded-full bg-violet-500 [animation-delay:0ms]"></span>
                                            <span className="h-1 w-1 animate-bounce rounded-full bg-violet-500 [animation-delay:120ms]"></span>
                                            <span className="h-1 w-1 animate-bounce rounded-full bg-violet-500 [animation-delay:240ms]"></span>
                                        </span>
                                    </span>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="grid h-full place-items-center px-6 text-center text-slate-500">
                            <div>
                                <MessagesSquare className="mx-auto h-10 w-10 text-brand-500" />
                                <h3 className="mt-4 text-lg font-semibold text-slate-900 dark:text-zinc-100">Pick a conversation</h3>
                                <p className="mt-1 text-sm">Select a chat on the left to start messaging. Your Rx verifications, doctor consults, support tickets, and AI assistant all live here.</p>
                            </div>
                        </div>
                    )}
                </div>
        </div>
    );
};

const ChatSendbird = () => {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    useEffect(() => {
        if (!loading && !user) navigate("/login?redirect=/chat", { replace: true });
    }, [user, loading, navigate]);

    return (
        <Layout>
            <div className="min-h-[calc(100vh-4rem)] bg-slate-50 dark:bg-zinc-950" data-testid="page-chat-sendbird">
                <Content />
            </div>
        </Layout>
    );
};

export default ChatSendbird;
